// <copyright company="Microsoft">Copyright (c) Microsoft. All rights reserved.</copyright>

import React, { useEffect, useState } from 'react';
import { Button, Text, Tooltip, makeStyles, mergeClasses, shorthands, tokens } from '@fluentui/react-components';
import { CheckmarkRegular, CopyRegular } from '@fluentui/react-icons';
import { RelatedCfdi, SUPPORTED_ISSUER_RFC } from './cfdiLookupService';

const useStyles = makeStyles({
    row: { display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 },
    uuid: {
        fontFamily: tokens.fontFamilyMonospace, overflowWrap: 'anywhere', minWidth: 0,
        flexGrow: 1, flexShrink: 1,
    },
    unavailable: { color: tokens.colorNeutralForeground3 },
    link: {
        fontFamily: tokens.fontFamilyMonospace, textAlign: 'left', justifyContent: 'flex-start',
        minWidth: 0, flexGrow: 1, flexShrink: 1, whiteSpace: 'normal', overflowWrap: 'anywhere',
        color: tokens.colorBrandForegroundLink,
        ...shorthands.padding('8px', '0'),
    },
    copy: { flexShrink: 0 },
    error: { display: 'block', color: tokens.colorPaletteRedForeground1 },
    announcement: {
        position: 'absolute', width: '1px', height: '1px', overflow: 'hidden',
        clipPath: 'inset(50%)', whiteSpace: 'nowrap',
    },
});

export default function CfdiUuidActions({ document, busy = false, onConsult }: {
    document: RelatedCfdi; busy?: boolean; onConsult?: (uuid: string) => void;
}) {
    const styles = useStyles();
    const [copyState, setCopyState] = useState<'idle' | 'copying' | 'copied' | 'error'>('idle');

    useEffect(() => {
        if (copyState !== 'copied') return;
        const timer = window.setTimeout(() => setCopyState('idle'), 2000);
        return () => window.clearTimeout(timer);
    }, [copyState]);

    async function copyUuid() {
        setCopyState('copying');
        try {
            await navigator.clipboard.writeText(document.uuid);
            setCopyState('copied');
        } catch {
            setCopyState('error');
        }
    }

    return <div>
        <div className={styles.row}>
            {document.canLookup === true && onConsult ?
                <Button appearance="transparent" className={styles.link} disabled={busy}
                    onClick={() => onConsult(document.uuid)}
                    aria-label={`Consult related CFDI ${document.uuid}`}>{document.uuid}</Button> :
                <code className={mergeClasses(styles.uuid, !document.canLookup && styles.unavailable)}
                    title={document.canLookup ? undefined : `Lookup unavailable: issuer is not confirmed as ${SUPPORTED_ISSUER_RFC}.`}>
                    {document.uuid}
                </code>}
            <Tooltip content={copyState === 'copied' ? 'UUID copied' : 'Copy UUID'} relationship="description">
                <Button appearance="subtle" size="small" className={styles.copy}
                    icon={copyState === 'copied' ? <CheckmarkRegular /> : <CopyRegular />}
                    aria-label={`Copy UUID ${document.uuid}`} disabled={copyState === 'copying'}
                    onClick={() => void copyUuid()} />
            </Tooltip>
        </div>
        <span role="status" className={styles.announcement}>{copyState === 'copied' ? 'UUID copied.' : ''}</span>
        {copyState === 'error' && <Text role="alert" size={200} className={styles.error}>
            Could not copy. Select the UUID and copy it manually.
        </Text>}
    </div>;
}
